import React, { useEffect, useState, useMemo } from "react";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { createStyles, makeStyles, useTheme, Theme } from "@material-ui/core/styles";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import { FormattedMessage } from "react-intl";
import GenericPageHeader from "legacy-components/page-headers/GenericPageHeader";
import InlineIconFormatter from "legacy-components/formatters/InlineIconFormatter";
import {Button, Link, Paper, Typography, Accordion, ButtonGroup, Tooltip} from "@material-ui/core";
import HelpOutlineOutlinedIcon from "@material-ui/icons/HelpOutlineOutlined";
import { useDispatch, useSelector } from "react-redux";
import ProxyEnvironment from "./proxyEnvironment";
import Helper from "pages/home/helper/Helper";
import EnvironmentHelper from "pages/home/helper/EnvironmentHelper";
import ResourceAuthorizer from "./authorizer/ResourceAuthorizer";
import ProxyVerification from "./verification/ProxyVerification";
import { map } from "rxjs/operators";
import {
  useResourceAbility,
  useFeatureAbility,
} from "libs/security/authorization/Permission";
import { Feature } from "libs/security/authorization/FeatureAbility";
import ProxyService from "libs/resources/proxy/ProxyService";
import {
  AsyncLoader,
  useAsyncAction,
} from "legacy-components/form/action/AsyncAction";
import {
  AccordionSummaryTitle,
  AccordionContent,
} from "legacy-components/form/components/accordion-form/AccordionForm";
import FormViewInliner from "legacy-components/form/container/FormViewInliner";
import NAWrap from "legacy-components/form/action/NAWrap";
import { StatusBadge } from "legacy-components/badges/StatusBadge";
import CopyToClipboard from "legacy-components/form/action/CopyToClipboard";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useHistory } from "react-router";
import { DEFAULT_IDENTITY_ENVIRONMENT, isNewConsoleSepFlow } from "header/environment-selector/ENV_CONSTANTS";
import AlertMUI from "@material-ui/lab/Alert";
import StarTick from "../../../../legacy-components/icons/StarTick";
import NewReleasesIcon from "@material-ui/icons/NewReleases";
import AudiencesView from "./AudiencesView";
import * as actions from "../../../../store/actions/actions";

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
  dir?: string;
}

function TabPanel({ children, value, index, dir, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      {...other}
    >
      {value === index && (
        <Container>
          <Box>{children}</Box>
        </Container>
      )}
    </div>
  );
}

interface Gateway {
  name: string;
  environments: Environment[];
}

interface Environment {
  environmentName: string;
}

interface ApiDeployment {
  environmentName: string;
  organisation: string;
  deployStatus: string;
  deploymentType?: string;
  deploymentLocation?: string;
  provider?: string;
}

interface ResourceServer {
  gateway_name: string;
}

interface ProxyData {
  apiName: string;
  version: string;
  description: string;
  createdBy: string;
  updatedBy: string;
  verified: boolean;
  isShared: boolean;
  apiDeployments: ApiDeployment[];
  resourceServer: ResourceServer[];
}

interface ProxyEnvironmentsProps {
  proxy: {
    apiName: string;
    audUsageStatus?: boolean;
  };
  onUpdate?: (data: any) => void;
}

const useStyles = makeStyles(({ palette }: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    title: {
      marginBottom: "15px",
    },
    tabs: {
      marginBottom: "30px",
    },
    container: {
      marginBottom: "10px",
    },
    icon: {
      color: palette.text.primary,
    },
    accordionSummary: {
      padding: "10px 15px",
      margin: 0,
      "&.Mui-expanded": {
        margin: " 0 !important",
        padding: "0 15px",
      },
    },
    accordionDetails: {
      padding: 0,
    },
  })
);

export default function ProxyEnvironments({ proxy, onUpdate }: ProxyEnvironmentsProps) {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [value, setValue] = useState(0);
  const [isShowEnvironmentHelper, toggleEnvironmentHelper] = useState(false);
  const [canShowResourceAuthorizer, setShowResourceAuthorizer] = useState<Record<string, boolean>>({});
  const history = useHistory();
  let gateways: Gateway[] | undefined = useSelector((state: any) => state?.common?.metadata?.gateways);

  const filteredGateways = useMemo(() => {
    return gateways?.filter((gateway) => gateway?.name?.toLowerCase() === DEFAULT_IDENTITY_ENVIRONMENT);
  }, [gateways]);
  
  gateways = isNewConsoleSepFlow ? filteredGateways : gateways;

  const [response, { subscribe }] = useAsyncAction<ProxyData>(
    () =>
      ProxyService.forApi(proxy?.apiName).pipe(
        // You may need to import 'map' from 'rxjs/operators'
        // and adjust the mapping logic as per your actual IAPIProxy structure
        // @ts-ignore
        map((apiProxy: any) => ({
          ...apiProxy,
          createdBy: apiProxy.createdBy ?? "",
          updatedBy: apiProxy.updatedBy ?? "",
          verified: apiProxy.verified ?? false,
          resourceServer: apiProxy.resourceServer ?? [],
        }))
      ),
  );

  const onSuccess = (data: any) => {
    subscribe();
    onUpdate?.(data);
  };

  const api = useSelector((state: any) => state?.proxyManager?.proxy);

  useEffect(() => {
    if(response.value && dispatch) {
      dispatch({ type: actions.storeProxy, payload: response.value });
    }
  },[response.value, dispatch]);
  
  const [hasInvite] = useResourceAbility({
    resource: ({ TeamMembers }: any) => TeamMembers.invite,
  });

  useEffect(() => {
    if (gateways && api?.apiDeployments) {
      gateways.forEach((gateway) => {
        canShowResourceAuthorizer[gateway.name] =
          api.apiDeployments.filter((deployment: ApiDeployment) => {
            return (
              deployment?.organisation?.toLowerCase() ===
              gateway?.name?.toLowerCase() && process.env.REACT_APP_NEW_ENVIRONMENT_SEPERATION ?
              deployment?.deployStatus === "DEPLOY_COMPLETED" : deployment?.deployStatus === "DEPLOY_COMPLETED" 
              && gateway?.name?.toLowerCase() === DEFAULT_IDENTITY_ENVIRONMENT
            );
          }).length > 0;
      });
      setShowResourceAuthorizer({ ...canShowResourceAuthorizer });
    }
  }, [api, gateways, hasInvite?.create()]);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };

  const [can, state] = useResourceAbility({
    resource: ({ Proxy }: any) => Proxy.verification.getStarted,
  });

  const hasFeature = useFeatureAbility({
    id: Feature.ApiVerification,
  });

  const CopyAPIName = (
    <CopyToClipboard size="small" edge="end" text={proxy?.apiName} />
  );

  return (
    api && (
        <div className={classes.root}>
          {gateways && (
            <Tabs
              value={value}
              onChange={handleChange}
              className={classes.tabs}
              indicatorColor="primary"
            >
              <Tab label="Summary" key="summary" />
              {gateways.map((gateway) => (
                <Tab
                  label={
                    <div>
                      {
                        gateway?.name?.toLowerCase() === "trimble-pre-prod" 
                          ? "Pre Prod"
                          : "Prod" 
                      }
                    </div>
                  }
                  key={gateway.name}
                />
              ))}
            </Tabs>
          )}
          {gateways && api && (
            <div>
              {gateways.map((gateway, index) => (
                <TabPanel
                  value={value}
                  index={index + 1}
                  dir={theme.direction}
                  key={index}
                >
                  <Container maxWidth="md" className={classes.container}>
                    <GenericPageHeader
                      title={
                        <Typography
                          variant="subtitle1"
                          className={classes.title}
                        >
                          <InlineIconFormatter
                            icon={
                              <HelpOutlineOutlinedIcon
                                onClick={() => {
                                  toggleEnvironmentHelper(true);
                                }}
                              />
                            }
                          >
                            <FormattedMessage defaultMessage="Environment" />
                          </InlineIconFormatter>
                        </Typography>
                      }
                    />
                    <Helper
                      open={isShowEnvironmentHelper}
                      handleClose={() => {
                        toggleEnvironmentHelper(false);
                      }}
                      children={<EnvironmentHelper />}
                    />
                    <div>
                      {api &&
                        gateway?.environments?.map((environment, envIndex) => {
                          let deployments =
                            api?.apiDeployments
                              .filter((apiDeployment: ApiDeployment) => {
                                const deployedEnv =
                                  apiDeployment?.deploymentType === 'region' &&
                                  apiDeployment?.environmentName?.toLowerCase() === 'prod'
                                    ? apiDeployment?.deploymentLocation
                                    : apiDeployment?.environmentName;

                                // If environment is dev/qa/stage, exclude deployments whose deploymentLocation is 'eu-west'
                                const envName = environment?.environmentName?.toLowerCase();
                                if (["dev", "qa", "stage"].includes(envName)) {
                                  return (
                                    deployedEnv === environment?.environmentName &&
                                    apiDeployment?.deploymentLocation !== "eu-west"
                                  );
                                }
                                return deployedEnv === environment?.environmentName;
                              })
                              // Sort deployments in ascending order based on deploymentType to show global deployments first
                              ?.sort((a: ApiDeployment, b: ApiDeployment) => {
                                if (!a.deploymentType && !b.deploymentType) return 0;
                                if (!a.deploymentType) return -1;
                                if (!b.deploymentType) return 1;
                                return a.deploymentType.localeCompare(b.deploymentType);
                              });
                          let deployment = deployments.length > 1 ?
                            deployments.filter(
                              (apiDeployment: ApiDeployment) => 
                                apiDeployment.provider === 'azure'
                            )?.[0] : 
                            deployments.length === 1 ? 
                              deployments[0] : 
                              null;
                          return (
                            api && (
                              <ProxyEnvironment
                                onSuccess={() => onSuccess(undefined)}
                                isShared={api?.isShared}
                                env={environment}
                                deployment={deployment}
                                key={envIndex}
                                onUpdate={onUpdate}
                              />
                            )
                          );
                        })}
                    </div>
                  </Container>

                  <Container maxWidth="md" className={classes.container}>
                    <div>
                      {<>
                        {api?.resourceServer.filter(
                          (resourceServer: ResourceServer) =>
                            resourceServer.gateway_name === gateway.name
                        ).length === 0 ? <></> :
                          <GenericPageHeader
                            title={
                              <Typography
                                variant="subtitle1"
                                className={classes.title}
                              >
                                <FormattedMessage defaultMessage="Scope Configurations" />
                              </Typography>
                            }
                          />
                        }
                        {
                          api?.resourceServer.filter(
                            (resourceServer: ResourceServer) =>
                              resourceServer.gateway_name === gateway.name
                          ).map((resources: any) => (
                            <ResourceAuthorizer
                              onSuccess={() => onSuccess(undefined)}
                              apiName={api?.apiName}
                              gatewayName={gateway?.name?.toLowerCase()}
                              resourceServer={resources || null}
                            />
                          ))
                        }
                      </>}
                    </div>
                  </Container>
                </TabPanel>
              ))}
              <TabPanel value={value} index={0}>
                <Container maxWidth="md" className={classes.container}>
                  <Accordion defaultExpanded={true}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      className={classes.accordionSummary}
                    >
                      <AccordionSummaryTitle>Details</AccordionSummaryTitle>
                    </AccordionSummary>

                    <AccordionDetails className={classes.accordionDetails}>
                      <AccordionContent>
                        <FormViewInliner
                          gutter="gutter-bottom"
                          title={
                            <FormattedMessage defaultMessage="API Proxy" />
                          }
                          controls={CopyAPIName}
                        >
                          <Typography variant="body1" className="no-overflow">
                            <NAWrap
                              value={api?.apiName}
                              showMessage={true}
                            >
                              {api?.apiName}
                            </NAWrap>
                          </Typography>
                        </FormViewInliner>
                        <FormViewInliner
                          gutter="gutter-bottom"
                          title={<FormattedMessage defaultMessage="Version" />}
                        >
                          <Typography variant="body1" className="no-overflow">
                            <NAWrap
                              value={api?.version}
                              showMessage={true}
                            >
                              {api?.version}
                            </NAWrap>
                          </Typography>
                        </FormViewInliner>
                        <FormViewInliner
                          gutter="gutter-bottom"
                          title={
                            <FormattedMessage defaultMessage="Description" />
                          }
                        >
                          <Typography variant="body1" className="no-overflow">
                            <NAWrap
                              value={api?.description}
                              showMessage={true}
                            >
                              {api?.description}
                            </NAWrap>
                          </Typography>
                        </FormViewInliner>
                        {hasInvite?.create() && (
                          <>
                            <FormViewInliner
                              gutter="gutter-bottom"
                              title={
                                <FormattedMessage defaultMessage="Created By" />
                              }
                            >
                              <Typography
                                variant="body1"
                                className="no-overflow"
                              >
                                <NAWrap
                                  value={api?.createdBy}
                                  showMessage={true}
                                >
                                  {api?.createdBy}
                                </NAWrap>
                              </Typography>
                            </FormViewInliner>
                            <FormViewInliner
                              gutter="gutter-bottom"
                              title={
                                <FormattedMessage defaultMessage="Updated By" />
                              }
                            >
                              <Typography
                                variant="body1"
                                className="no-overflow"
                              >
                                <NAWrap
                                  value={api?.updatedBy}
                                  showMessage={true}
                                >
                                  {api?.updatedBy}
                                </NAWrap>
                              </Typography>
                            </FormViewInliner>
                          </>
                        )}
                        <FormViewInliner
                          gutter="gutter-bottom"
                          title={<FormattedMessage defaultMessage="Status" />}
                        >
                          <Typography variant="body1" component={"div"} className="no-overflow">
                            <StatusBadge
                              align="right"
                              status={{
                                kind: "access",
                                key: api?.verified
                                  ? "VERIFIED"
                                  : "UNVERIFIED",
                              }}
                            />
                          </Typography>
                        </FormViewInliner>
                      </AccordionContent>
                    </AccordionDetails>
                  </Accordion>
                </Container>
              </TabPanel>
            </div>
          )}
        </div>
    )
  );
}
